import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { getFacilityUsageSlotsAsync } from "src/api/contract/facility-usage-slot-api";
import { FacilityUsageSlotListParams, FacilityUsageSlotSimple } from "src/api/contract/facility-usage-slot-types";
import { useApiOperation } from "src/api/hooks";
import { PageMeta } from "src/api/public-types";
import { useModal } from "src/recoil/modalState/hook";
import { qsParse } from "src/utils";
import { YmdFormat } from "src/utils/common-util";
import { downloadExcelByColumns } from "src/utils/excelDownload";
import { settlementSearchTypeOptions } from "../commonSpaceSettlement-types";
import { commonSpaceSettlementColumns } from "../list/columns/CommonSpaceSettlementColumns";

export type SettlementQueryParams = FacilityUsageSlotListParams & {
  buildingName?: string; // 화면 표시용
  contractApplyNumber?: string; // 화면 표시용
  contractManageId?: string; // 화면 표시용 (계약선택 입력칸)
};

// 예약 상태(화면) → 예약 상태 코드 + 예약 시각 비교 파라미터 (공용공간 예약 화면과 동일 조합, 기준시각은 호출 시점)
const toReservationStatusParams = (reservationStatus: string): FacilityUsageSlotListParams => {
  const now = moment().format(YmdFormat.WITH_TIME_ZONE);
  switch (reservationStatus) {
    case "RESERVATION_FAILED": // 예약실패
      return { reservationStatusCodes: "INCOMPLETE" };
    case "WAITING_FOR_USE": // 이용대기
      return { reservationStatusCodes: "ACKNOWLEDGED", reservationStartTimeGt: now };
    case "IN_USE": // 이용중
      return { reservationStatusCodes: "ACKNOWLEDGED", reservationStartTimeLte: now, reservationEndTimeGt: now };
    case "USE_COMPLETED": // 이용완료
      return { reservationStatusCodes: "ACKNOWLEDGED", reservationEndTimeLte: now };
    case "USE_CANCELLED": // 이용취소
      return { reservationStatusCodes: "CANCELLED" };
    default:
      return {};
  }
};

// 화면 표시 전용 파라미터를 제거하고 API 파라미터만 추출
const toApiParams = (params: SettlementQueryParams): FacilityUsageSlotListParams => {
  const {
    buildingId,
    contractId,
    searchTimeType,
    searchStartTime,
    searchEndTime,
    reservationStatus,
    usageStatus,
    searchType,
    searchValue,
    page,
    size,
    sort,
  } = params;
  const apiParams: FacilityUsageSlotListParams = { page, size };
  if (buildingId) apiParams.buildingId = Number(buildingId);
  if (contractId) apiParams.contractId = Number(contractId);
  // 기간: 선택일 00:00:00 ~ 23:59:59 로 확장 전달 (미선택 시 미전달 → 전 기간 조회)
  if (searchStartTime && searchEndTime) {
    apiParams.searchTimeType = searchTimeType || "USAGE_PERIOD";
    apiParams.searchStartTime = moment(searchStartTime).startOf("day").format(YmdFormat.WITH_TIME_ZONE);
    apiParams.searchEndTime = moment(searchEndTime).endOf("day").format(YmdFormat.WITH_TIME_ZONE);
  }
  if (reservationStatus) Object.assign(apiParams, toReservationStatusParams(reservationStatus));
  if (usageStatus) apiParams.usageStatuses = usageStatus;
  if (searchValue) {
    // 목록 밖 검색 타입(기존 URL 의 facilityName 등)은 전체로 조회
    const isValidSearchType = settlementSearchTypeOptions.some((option) => option.value === searchType);
    apiParams.searchType = isValidSearchType ? searchType : "all";
    apiParams.searchValue = searchValue;
  }
  // 정렬 미선택 시 서버 기본 정렬 (정산 ID desc)
  if (sort?.orders?.length) apiParams.sort = sort;
  return apiParams;
};

/**
 * 공용공간 정산 목록 조회 / 엑셀 다운로드 커스텀 훅 (RCL31)
 */
const useCommonSpaceSettlement = () => {
  const location = useLocation();
  const { setBaseModal } = useModal();

  // URL query parameter -> 조회 파라미터
  const settlementQueryParam: SettlementQueryParams = useMemo(() => {
    const _queryParams: SettlementQueryParams = qsParse(location.search);

    if (!_queryParams?.page) _queryParams.page = 0;
    if (!_queryParams?.size) _queryParams.size = 20;
    // 메뉴 진입 시 당월 1일 ~ 말일 소진기간으로 사전 로딩 (기간 해제 시 빈 값이 전달되어 전체 기간 조회)
    if (_queryParams.searchStartTime === undefined && _queryParams.searchEndTime === undefined) {
      _queryParams.searchStartTime = moment().startOf("month").format(YmdFormat.YYYY_MM_DD);
      _queryParams.searchEndTime = moment().endOf("month").format(YmdFormat.YYYY_MM_DD);
    }
    if (!_queryParams?.searchTimeType) _queryParams.searchTimeType = "USAGE_PERIOD";
    // 예약 상태 / 정산 상태 기본값: "전체"(빈 값) — 미지정 시 전체검색

    return _queryParams;
  }, [location]);

  const [settlementList, setSettlementList] = useState<FacilityUsageSlotSimple[]>();
  const [pageMeta, setPageMeta] = useState<PageMeta>();

  const { executeAsync: getFacilityUsageSlots } = useApiOperation(getFacilityUsageSlotsAsync);

  useEffect(() => {
    fetchList(settlementQueryParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementQueryParam]);

  const fetchList = async (params: SettlementQueryParams) => {
    const {
      data: {
        data: { content },
        meta,
      },
    } = await getFacilityUsageSlots(toApiParams(params));

    setSettlementList(content || []);
    setPageMeta(meta.pageMeta);
  };

  const showDownloadResult = (success: boolean, error?: any) => {
    setBaseModal({
      isOpen: true,
      title: success ? "엑셀 다운로드가 완료되었습니다." : "엑셀 다운로드에 실패하였습니다.",
      ...(error && { children: String(error) }),
      btnRightTitle: "확인",
      onClick: () => setBaseModal({ isOpen: false }),
    });
  };

  /** 공용공간 정산 내역 엑셀 다운로드 (전체 페이지 조회 후 다운로드) */
  const onDownloadExcel = async (passParams: SettlementQueryParams) => {
    try {
      const defaultSize = 1000;
      const totalElements = pageMeta?.totalElements || 0;
      const totalPages = Math.max(1, Math.ceil(totalElements / defaultSize));
      const baseParams = toApiParams({ ...passParams, size: defaultSize });

      const responses = await Promise.all(
        Array.from({ length: totalPages }, (_, i) => getFacilityUsageSlots({ ...baseParams, page: i })),
      );
      const allRows = responses.flatMap((response) => response.data?.data?.content || []);

      const { searchStartTime, searchEndTime } = passParams;
      const period =
        searchStartTime && searchEndTime ? `-${moment(searchStartTime).format("YYYYMMDD")}~${moment(searchEndTime).format("YYYYMMDD")}` : "";

      await downloadExcelByColumns<FacilityUsageSlotSimple>({
        data: allRows,
        columns: commonSpaceSettlementColumns,
        fileNamePrefix: `공용공간정산${period}`,
        onNoData: () => {
          setBaseModal({
            isOpen: true,
            title: "조회된 데이터가 없습니다.",
            btnRightTitle: "확인",
            onClick: () => setBaseModal({ isOpen: false }),
          });
        },
        onSuccess: () => showDownloadResult(true),
        onError: (error: any) => showDownloadResult(false, error),
      });
    } catch (error) {
      showDownloadResult(false, error);
    }
  };

  return {
    settlementQueryParam,
    settlementList,
    pageMeta,
    onDownloadExcel,
  };
};

export default useCommonSpaceSettlement;
